import AuthApi from '../api/AuthApi';
import OrganisationApi from '../api/organizationApi';

const ALIGNMENT_LEFT = 'left';

// The Intercom APP ID (never changes)
const INTERCOM_APP_ID = process.env.REACT_APP_INTERCOM_APP_ID;

// Tracks whether Intercom has been booted
let isBooted = false;

/**
 * Build the user + company (clinic) config for Intercom.
 */
async function buildUserConfig(user) {
    if (!user) {
        user = await AuthApi.getMe();
    }

    const config = {
        name: user.displayName,
        email: user.email.address,
        created_at: user.createdAt.valueOf()
    };

    // Attach clinic/company metadata correctly
    const organisation = await OrganisationApi.getOrg();
    const orgData = organisation?.data ?? organisation;
    if (orgData?.intercomId) {
        config.company = {
            id: orgData.intercomId,
            name: orgData.name
        };
    }

    return config;
}

const Intercom = {
    /**
     * Fully shut down Intercom.
     */
    shutDown() {
        if (!window.Intercom) return;
        window.Intercom('shutdown');
        isBooted = false;
    },

    hideLauncher() {
        if (!window.Intercom) return;
        window.Intercom('hide');
        window.Intercom('update', { hide_default_launcher: true });
    },

    showLauncher() {
        if (!window.Intercom) return;
        window.Intercom('update', { hide_default_launcher: false });
    },

    /**
     * Boot Intercom for a user.
     */
    async boot(config) {
        if (!window.Intercom) return;
        if (!INTERCOM_APP_ID) return;

        if (isBooted) {
            return this.update(config);
        }

        window.Intercom('boot', {
            ...config,
            app_id: INTERCOM_APP_ID,
            alignment: ALIGNMENT_LEFT
        });

        isBooted = true;
    },

    /**
     * Update Intercom only if already booted.
     */
    async update(config) {
        if (!window.Intercom || !isBooted) return;
        if (!INTERCOM_APP_ID) return;

        window.Intercom('update', {
            ...config,
            app_id: INTERCOM_APP_ID,
            alignment: ALIGNMENT_LEFT
        });
    },

    /**
     * Update or boot Intercom for the current user.
     */
    async updateUser(user = null) {
        const config = await buildUserConfig(user);

        if (isBooted) {
            this.update(config);
        } else {
            this.boot(config);
        }
    },

    /**
     * Load the Intercom widget.
     */
    async init() {
        return new Promise((resolve, reject) => {
            (async () => {
                try {
                    if (!INTERCOM_APP_ID) {
                        return reject(new Error('REACT_APP_INTERCOM_APP_ID is not configured'));
                    }

                    const w = window;
                    const ic = w.Intercom;

                    if (typeof ic === 'function') {
                        ic('reattach_activator');
                        ic('update', w.intercomSettings);
                        return resolve();
                    }

                    const d = document;
                    const i = function() {
                        i.c(arguments);
                    };
                    i.q = [];
                    i.c = function(args) {
                        i.q.push(args);
                    };
                    w.Intercom = i;

                    const load = () => {
                        const s = d.createElement('script');
                        s.type = 'text/javascript';
                        s.async = true;
                        s.src = `https://widget.intercom.io/widget/${INTERCOM_APP_ID}`;
                        const x = d.getElementsByTagName('script')[0];
                        x.parentNode.insertBefore(s, x);
                        resolve();
                    };

                    if (d.readyState === 'complete') load();
                    else w.addEventListener('load', load, false);
                } catch (err) {
                    reject(err instanceof Error ? err : new Error(String(err)));
                }
            })();
        });
    }
};

export default Intercom;
